import {
  Button,
  Box,
  CircularProgress,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  TextField,
  Theme,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import {
  Check,
  ChevronLeft,
  ChevronRight,
  ExpandMore,
  PersonOutline,
  SwapVert,
} from "@mui/icons-material";
import Layout from "components/Layout";
import ManagementPageShell from "components/management/ManagementPageShell";
import CustomScrollbars from "components/CustomScrollbars";
import MajorJobsPanel from "containers/MajorJobsPanel";
import TaskWorkspace from "containers/TaskDetailsPage";
import ConnectionsPanel from "containers/ConnectionsPanel";
import {
  useGetBackendUsersQuery,
  useLazyGetBackendUserBotConnectionsQuery,
} from "features/adminUsers/adminUsers";
import { BotsManagementWsEvent, botsManagementWs } from "features/ws/botsManagementWs";
import { useViewAs } from "hooks/useViewAs";
import { isSuperAdmin } from "utils/roles";
import {
  ChangeEvent,
  MouseEvent,
  UIEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useParams } from "react-router-dom";
import useAppNavigate from "hooks/useAppNavigate";
import { AdminVendorConnection, BackendUser } from "types/interfaces/adminUsers";

const DIALOG_PAPER_NO_SPINNER_SX = {
  "& input[type=number]": { MozAppearance: "textfield" as const },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
    WebkitAppearance: "none",
    margin: 0,
  },
} as const;

/** Display the backend user's own name (first/last), falling back to the username — never the client. */
function backendUserDisplayName(
  user: Pick<BackendUser, "first_name" | "last_name" | "username">,
): string {
  const fullName = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return fullName || user.username;
}

/** Narrow strip when user list is collapsed — avoids `fit-content` width jumps during transition */
const USERS_PANEL_COLLAPSED_PX = 48;

/**
 * The orders an operator can put the user list in. Sorting has to happen on the
 * server: the list is paged by infinite scroll, so an alphabet over the pages
 * loaded so far would be a false one.
 */
const USERS_SORT_OPTIONS = [
  { value: "name_asc", label: "Name (A-Z)", sortBy: "name", sortOrder: "asc" },
  { value: "name_desc", label: "Name (Z-A)", sortBy: "name", sortOrder: "desc" },
  {
    value: "newest",
    label: "Newest first",
    sortBy: "date_joined",
    sortOrder: "desc",
  },
  {
    value: "oldest",
    label: "Oldest first",
    sortBy: "date_joined",
    sortOrder: "asc",
  },
] as const;

type UsersSortValue = (typeof USERS_SORT_OPTIONS)[number]["value"];

const DEFAULT_USERS_SORT: UsersSortValue = "name_asc";
const BOTS_MGMT_USERS_SORT_KEY = "bots_management_users_sort";

function usersSortOption(value: UsersSortValue) {
  return USERS_SORT_OPTIONS.find((option) => option.value === value) ?? USERS_SORT_OPTIONS[0];
}

/** A stored value that is no longer one of the options falls back to A-Z. */
function readStoredUsersSort(): UsersSortValue {
  const stored = localStorage.getItem(BOTS_MGMT_USERS_SORT_KEY);
  return USERS_SORT_OPTIONS.some((option) => option.value === stored)
    ? (stored as UsersSortValue)
    : DEFAULT_USERS_SORT;
}

/** Shared look of the small icon buttons in the user panel header. */
const panelIconButtonSx = (theme: Theme) => ({
  flexShrink: 0,
  padding: "6px",
  color: theme.palette.primary.main,
  backgroundColor: "transparent",
  transition: theme.transitions.create(["color", "background-color", "transform"], {
    duration: theme.transitions.duration.shorter,
  }),
  "&:hover": {
    color: theme.palette.primary.dark,
    backgroundColor: theme.palette.action.hover,
    transform: "scale(1.08)",
  },
  "& .MuiSvgIcon-root": {
    fontSize: "1.5rem",
  },
});

/** Sort control for the user list — one in the sidebar, one in the mobile drawer. */
function UsersSortButton({
  value,
  onChange,
}: {
  value: UsersSortValue;
  onChange: (next: UsersSortValue) => void;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const active = usersSortOption(value);
  return (
    <>
      <Tooltip title={`Sort: ${active.label}`}>
        <IconButton
          onClick={(event: MouseEvent<HTMLElement>) => setAnchor(event.currentTarget)}
          aria-label="Sort users"
          sx={panelIconButtonSx}
        >
          <SwapVert />
        </IconButton>
      </Tooltip>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)}>
        {USERS_SORT_OPTIONS.map((option) => (
          <MenuItem
            key={option.value}
            selected={option.value === value}
            onClick={() => {
              setAnchor(null);
              if (option.value !== value) onChange(option.value);
            }}
          >
            <ListItemIcon>
              {option.value === value ? <Check fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{option.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

export default function BotsManagementPage() {
  const {
    profile: botsProfile,
    readOnly: botsReadOnly,
    basePath,
  } = useViewAs();
  const canEditBots = isSuperAdmin(botsProfile) && !botsReadOnly;
  const theme = useTheme();
  // Phone layout: the user list and the workspace swap instead of sitting side by side.
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const navigate = useAppNavigate();
  const { id, connectionId, majorJobId, taskId } = useParams<{
    id?: string;
    connectionId?: string;
    majorJobId?: string;
    taskId?: string;
  }>();
  const location = useLocation();
  /** True when the user is on the dedicated bot-connections screen for the selected user. */
  const isConnectionsScreen = useMemo(
    () => /\/bots-management\/\d+\/connections\/?$/.test(location.pathname),
    [location.pathname],
  );
  const routedMajorJobPk =
    majorJobId != null && majorJobId !== "" && Number.isFinite(Number(majorJobId))
      ? Number(majorJobId)
      : null;
  const selectedConnectionId =
    connectionId != null && connectionId !== "" && Number.isFinite(Number(connectionId))
      ? Number(connectionId)
      : null;
  const routedTaskId =
    taskId != null && taskId !== "" && Number.isFinite(Number(taskId))
      ? Number(taskId)
      : null;

  const [search, setSearch] = useState("");
  const [usersSort, setUsersSort] = useState<UsersSortValue>(readStoredUsersSort);
  const [usersPage, setUsersPage] = useState(1);
  const [loadedBackendUsers, setLoadedBackendUsers] = useState<BackendUser[]>([]);
  const [usersPanelOpen, setUsersPanelOpen] = useState(true);
  const [mobileUsersDrawerOpen, setMobileUsersDrawerOpen] = useState(false);
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  // The desktop collapse toggle makes no sense on phones — keep the list expanded there.
  const usersListExpanded = usersPanelOpen || isMobile;

  const BOTS_MGMT_LAST_URL_KEY = "bots_management_last_url";

  // Restore last visited URL on first mount when no id is provided.
  // Client view neither reads nor writes it: the stored URL belongs to the
  // admin's own session, and following it would leave the preview shell.
  const didRestoreRef = useRef(false);
  useEffect(() => {
    if (didRestoreRef.current) return;
    didRestoreRef.current = true;
    if (basePath) return;
    if (!id || id === "") {
      const saved = localStorage.getItem(BOTS_MGMT_LAST_URL_KEY);
      if (saved) {
        navigate(saved, { replace: true });
      }
    }
  }, [id, navigate, basePath]);

  // Save the current meaningful URL (including ?tab=... and other search params) whenever navigation happens
  useEffect(() => {
    if (basePath) return;
    if (id && id !== "") {
      localStorage.setItem(BOTS_MGMT_LAST_URL_KEY, location.pathname + location.search);
    }
  }, [location.pathname, location.search, id, basePath]);

  useEffect(() => {
    if (id != null && id !== "") {
      const n = Number(id);
      setSelectedUserId(Number.isFinite(n) ? n : null);
    } else {
      setSelectedUserId(null);
    }
  }, [id]);

  const activeUsersSort = usersSortOption(usersSort);
  const {
    data: backendUsersData,
    isLoading: usersLoading,
    isFetching: usersFetching,
  } = useGetBackendUsersQuery({
    page: usersPage,
    page_size: 50,
    search: search || undefined,
    sort_by: activeUsersSort.sortBy,
    sort_order: activeUsersSort.sortOrder,
  });
  const backendUsers: BackendUser[] = loadedBackendUsers;
  const hasMoreBackendUsers = Boolean(backendUsersData?.next);

  // After a page refresh the URL restores the selected user id, but that user may
  // not be on the current (empty-search) page — fetch them directly by id so the
  // workspace opens without forcing a manual re-search. Decided only once the
  // page query has answered: firing while it is still in flight raced it with
  // a second /api/admin/users/ request on every load (the user was almost
  // always on the first page anyway), and loadedBackendUsers fills one render
  // after the response, so the response itself is checked too.
  const needsUserByIdFallback =
    selectedUserId != null &&
    !usersLoading &&
    !usersFetching &&
    !loadedBackendUsers.some((u) => u.id === selectedUserId) &&
    !(backendUsersData?.results ?? []).some((u) => u.id === selectedUserId);
  const { data: selectedUserByIdData } = useGetBackendUsersQuery(
    { id: selectedUserId ?? undefined, page: 1, page_size: 1 },
    { skip: !needsUserByIdFallback },
  );
  const selectedUserById =
    selectedUserByIdData?.results?.find((u) => u.id === selectedUserId) ?? null;

  const [
    loadBotConnections,
    {
      data: botConnectionsData,
      isLoading: isConnectionsLoadingInitial,
      isFetching: isConnectionsFetching,
      isError: connectionsError,
    },
  ] = useLazyGetBackendUserBotConnectionsQuery();
  const connectionsLoading = isConnectionsLoadingInitial || isConnectionsFetching;

  const selectedUser = useMemo(
    () => backendUsers.find((u) => u.id === selectedUserId) ?? selectedUserById,
    [backendUsers, selectedUserId, selectedUserById],
  );
  const botConnections: AdminVendorConnection[] = botConnectionsData?.connections ?? [];
  const usersScrollRef = useRef<HTMLDivElement | HTMLUListElement | null>(null);
  const mobileUsersScrollRef = useRef<HTMLDivElement | null>(null);
  const selectedUserIdRef = useRef<number | null>(selectedUserId);
  const selectedConnectionIdRef = useRef<number | null>(selectedConnectionId);
  const refetchConnectionsRef = useRef<() => Promise<unknown>>(async () => Promise.resolve());
  useEffect(() => {
    setUsersPage(1);
  }, [search]);

  useEffect(() => {
    const nextUsers = backendUsersData?.results;
    // Nothing has answered for the current arguments yet (a new sort or a new
    // search): keep the rows on screen. Emptying the list here unresolved the
    // selected user, which blanked and remounted the whole workspace.
    if (nextUsers == null) return;
    setLoadedBackendUsers((prev) => {
      if (usersPage === 1) return nextUsers;
      const existingIds = new Set(prev.map((user) => user.id));
      return [...prev, ...nextUsers.filter((user) => !existingIds.has(user.id))];
    });
  }, [backendUsersData?.results, usersPage]);

  useEffect(() => {
    selectedUserIdRef.current = selectedUserId;
  }, [selectedUserId]);

  useEffect(() => {
    selectedConnectionIdRef.current = selectedConnectionId;
  }, [selectedConnectionId]);

  useEffect(() => {
    refetchConnectionsRef.current = async () => {
      if (selectedUserIdRef.current == null) return;
      await loadBotConnections(selectedUserIdRef.current, false);
    };
  }, [loadBotConnections]);

  useEffect(() => {
    if (selectedUserId == null) return;
    void loadBotConnections(selectedUserId, false);
  }, [selectedUserId, loadBotConnections]);

  useEffect(() => {
    // The first "connected" after the page load is not a reconnect: the
    // connections (and, on the connection view, the tasks) were just loaded
    // over REST, and refetching them again exactly when the socket opened was
    // one of the duplicates CAL-5907 measured. Only a REconnect - the socket
    // died and came back - needs a catch-up refetch.
    const hasSeenConnectedRef = { current: botsManagementWs.isConnected() };
    const unsubscribeStatus = botsManagementWs.subscribeStatus((connected) => {
      if (!connected) return;
      if (!hasSeenConnectedRef.current) {
        hasSeenConnectedRef.current = true;
        return;
      }
      refetchConnectionsRef.current();
    });
    // A busy run emits an event per status write; refetching two endpoints on
    // every one of them is where the un-debounced bot-connections traffic came
    // from. Coalesce leading-edge: the first event of a window schedules the
    // refetch, the rest ride it.
    let wsRefetchTimer: ReturnType<typeof setTimeout> | null = null;
    const unsubscribeEvents = botsManagementWs.subscribe((payload: BotsManagementWsEvent) => {
      const task = payload.task;
      if (!task) return;
      const eventUserId = task.user_id == null ? null : Number(task.user_id);
      const selectedUser = selectedUserIdRef.current == null ? null : Number(selectedUserIdRef.current);
      if (selectedUser == null) return;
      if (eventUserId != null && eventUserId !== selectedUser) return;
      if (
        selectedConnectionIdRef.current != null &&
        task.connection_id != null &&
        Number(task.connection_id) !== Number(selectedConnectionIdRef.current)
      ) {
        return;
      }
      if (wsRefetchTimer) return;
      wsRefetchTimer = setTimeout(() => {
        wsRefetchTimer = null;
        refetchConnectionsRef.current();
      }, 1000);
    });
    return () => {
      unsubscribeStatus();
      unsubscribeEvents();
      if (wsRefetchTimer) clearTimeout(wsRefetchTimer);
    };
  }, []);

  const handleSelectUser = (userId: number) => {
    navigate(`/bots-management/${userId}`);
  };

  const handleChangeUsersSort = (next: UsersSortValue) => {
    // Reset the page here rather than in an effect: React batches both updates,
    // so the request goes out for page 1 instead of re-fetching the page the
    // old order was on. A new order also starts at its own beginning, and the
    // list shrinking back to one page would otherwise leave the scroller near
    // the bottom and pull the next page in at once.
    setUsersSort(next);
    setUsersPage(1);
    if (usersScrollRef.current) usersScrollRef.current.scrollTop = 0;
    if (mobileUsersScrollRef.current) mobileUsersScrollRef.current.scrollTop = 0;
    localStorage.setItem(BOTS_MGMT_USERS_SORT_KEY, next);
  };

  const handleUsersListScroll = (event: UIEvent<HTMLElement>) => {
    const element = event.currentTarget;
    const nearBottom =
      element.scrollTop + element.clientHeight >= element.scrollHeight - 48;
    if (nearBottom && hasMoreBackendUsers && !usersFetching) {
      setUsersPage((page) => page + 1);
    }
  };

  /** Canonical vendor folder slug under S3 is users/{id}/{vendor}/data/{slug}. */
  const parseBotStoragePath = (currentPath: string, taskVendor: string) => {
    const uid = String(selectedUserId ?? "user");
    const defaultPrefix = `storage_bucket/${uid}/${taskVendor}/data`;
    const norm = (currentPath || "").trim();
    if (!norm) {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    const segs = norm.split("/").filter(Boolean);
    let u: string;
    let v: string;
    let rest: string[];
    if (segs[0] === "users" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else if (segs[0] === "storage_bucket" && segs.length >= 3) {
      u = segs[1];
      v = segs[2];
      rest = segs.slice(3);
    } else {
      return { prefix: defaultPrefix, folderRest: "" };
    }
    let folderSlug = "";
    if (rest.length && rest[0] === "data") {
      folderSlug = rest.slice(1).join("/");
    } else {
      folderSlug = rest.join("/");
    }
    return { prefix: `storage_bucket/${u}/${v}/data`, folderRest: folderSlug };
  };

  const buildPathPrefix = (currentPath: string, taskVendor: string) =>
    parseBotStoragePath(currentPath, taskVendor).prefix;

  return (
    <Layout title="Bots Management" content="Bots Management" noScroll>
      <ManagementPageShell title="Bots Management">
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flex: 1,
            minHeight: 0,
            "& input[type=number]": { MozAppearance: "textfield" },
            "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button": {
              WebkitAppearance: "none",
              margin: 0,
            },
          }}
        >
          <Paper
            variant="outlined"
            sx={(theme) => ({
              // Mobile: the list is the whole screen while no user is picked, hidden afterwards.
              width: usersListExpanded
                ? { xs: "100%", sm: 280, md: 340 }
                : USERS_PANEL_COLLAPSED_PX,
              flexShrink: 0,
              alignSelf: usersListExpanded ? "stretch" : "flex-start",
              p: usersListExpanded ? 1.5 : 0.5,
              display: {
                xs: selectedUserId == null ? "flex" : "none",
                sm: "flex",
              },
              flexDirection: "column",
              minHeight: usersListExpanded ? 0 : "auto",
              overflow: "hidden",
              transition: theme.transitions.create(["width", "padding"], {
                easing: theme.transitions.easing.easeInOut,
                duration: 280,
              }),
              willChange: "width",
            })}
          >
            {usersListExpanded ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                  minHeight: 0,
                  minWidth: 0,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1.5,
                    minWidth: 0,
                  }}
                >
                  <TextField
                    size="small"
                    placeholder="Search users..."
                    value={search}
                    onChange={(e: ChangeEvent<HTMLInputElement>) =>
                      setSearch(e.target.value)
                    }
                    fullWidth
                    sx={{ flex: 1, minWidth: 0 }}
                  />
                  <UsersSortButton value={usersSort} onChange={handleChangeUsersSort} />
                  <Tooltip title="Collapse user list" placement="right">
                    <IconButton
                      onClick={() => setUsersPanelOpen(false)}
                      aria-label="Collapse user list"
                      aria-expanded={true}
                      sx={(theme) => ({
                        ...panelIconButtonSx(theme),
                        display: { xs: "none", sm: "inline-flex" },
                      })}
                    >
                      <ChevronLeft />
                    </IconButton>
                  </Tooltip>
                </Box>
                <CustomScrollbars
                  ref={usersScrollRef}
                  sx={{ flex: 1, minHeight: 0 }}
                  preserveScrollOnGrow
                  scrollableProps={{ onScroll: handleUsersListScroll }}
                >
                  {usersLoading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : (
                    <List disablePadding>
                      {backendUsers.map((user: BackendUser) => (
                        <ListItemButton
                          key={user.id}
                          selected={selectedUserId === user.id}
                          onClick={() => handleSelectUser(user.id)}
                          sx={{ borderRadius: 1, mb: 0.5 }}
                        >
                          <ListItemText
                            primary={backendUserDisplayName(user)}
                            primaryTypographyProps={{ fontWeight: 700 }}
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </CustomScrollbars>
              </Box>
            ) : (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                  pt: 0.25,
                }}
              >
                <Tooltip title="Expand user list" placement="right">
                  <IconButton
                    onClick={() => setUsersPanelOpen(true)}
                    aria-label="Expand user list"
                    aria-expanded={false}
                    sx={panelIconButtonSx}
                  >
                    <ChevronRight />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              flex: 1,
              p: 1.5,
              minWidth: 0,
              display: {
                xs: selectedUserId == null ? "none" : "flex",
                sm: "flex",
              },
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {selectedUser != null && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setMobileUsersDrawerOpen(true)}
                startIcon={<PersonOutline />}
                endIcon={<ExpandMore />}
                sx={{
                  display: { xs: "flex", sm: "none" },
                  mb: 1.5,
                  flexShrink: 0,
                  minHeight: 40,
                  textTransform: "none",
                  fontWeight: 700,
                  justifyContent: "flex-start",
                  "& .MuiButton-endIcon": { ml: "auto" },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {backendUserDisplayName(selectedUser)}
                </Box>
              </Button>
            )}
            {selectedUser == null ? (
              <Typography color="text.secondary">
                Select a user to manage major jobs.
              </Typography>
            ) : routedTaskId != null ? (
              <TaskWorkspace
                taskId={routedTaskId}
                userId={selectedUser.id}
                onBack={(fallbackPath, jobId) => {
                  // Return to where the task was opened from (carried in ?back=),
                  // not the browser history — side-navigation must not misroute us.
                  const raw = new URLSearchParams(location.search).get("back");
                  const target = raw ? decodeURIComponent(raw) : "";
                  // The writer stores location.pathname, so the value is
                  // already shell-qualified - compare it as such.
                  if (target.startsWith(`${basePath}/bots-management/`)) {
                    // Land with the task's job expanded when the destination
                    // is a major job workspace that does not say so already.
                    const withJob =
                      jobId != null &&
                      /\/major-job\/\d+/.test(target) &&
                      !/[?&]job=/.test(target)
                        ? `${target}${target.includes("?") ? "&" : "?"}job=${jobId}`
                        : target;
                    navigate(withJob);
                  } else if (fallbackPath) {
                    // A directly opened task link carries no ?back=; its
                    // natural home is the major job workspace with its job
                    // expanded (the per-connection jobs view is retired).
                    navigate(fallbackPath);
                  } else {
                    navigate(`/bots-management/${selectedUser.id}`);
                  }
                }}
                onManageConnections={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            ) : isConnectionsScreen ? (
              <ConnectionsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onBack={() => navigate(`/bots-management/${selectedUser.id}`)}
                canEdit={canEditBots}
              />
            ) : routedMajorJobPk != null ? (
              <MajorJobsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                detailMajorJobId={routedMajorJobPk}
                onExitMajorDetail={() => navigate(`/bots-management/${selectedUser.id}`)}
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            ) : (
              <MajorJobsPanel
                userId={selectedUser.id}
                userLabel={backendUserDisplayName(selectedUser)}
                connections={botConnections}
                connectionsLoading={connectionsLoading}
                connectionsError={Boolean(connectionsError)}
                onRefreshConnections={async () => refetchConnectionsRef.current()}
                onOpenMajorDetail={(mjId) =>
                  navigate(`/bots-management/${selectedUser.id}/major-job/${mjId}`)
                }
                onOpenConnectionsScreen={() =>
                  navigate(`/bots-management/${selectedUser.id}/connections`)
                }
              />
            )}
          </Paper>
        </Box>
      {/* Mobile user picker — mirrors the sidebar list, styled like other mobile drawers. */}
      <Drawer
        anchor="bottom"
        open={isMobile && mobileUsersDrawerOpen}
        onClose={() => setMobileUsersDrawerOpen(false)}
        PaperProps={{
          sx: {
            borderTopLeftRadius: 2,
            borderTopRightRadius: 2,
            height: "min(88vh, 560px)",
            px: 2,
            py: 2,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5, flexShrink: 0 }}>
          Backend users
        </Typography>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            mb: 1.5,
            flexShrink: 0,
            minWidth: 0,
          }}
        >
          <TextField
            size="small"
            placeholder="Search users..."
            value={search}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setSearch(e.target.value)}
            fullWidth
            sx={{ flex: 1, minWidth: 0 }}
          />
          <UsersSortButton value={usersSort} onChange={handleChangeUsersSort} />
        </Box>
        <Box
          ref={mobileUsersScrollRef}
          sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}
          onScroll={handleUsersListScroll}
        >
          {usersLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <List disablePadding>
              {backendUsers.map((user: BackendUser) => (
                <ListItemButton
                  key={user.id}
                  selected={selectedUserId === user.id}
                  onClick={() => {
                    setMobileUsersDrawerOpen(false);
                    handleSelectUser(user.id);
                  }}
                  sx={{ borderRadius: 1, mb: 0.5, minHeight: 44 }}
                >
                  <ListItemText
                    primary={backendUserDisplayName(user)}
                    primaryTypographyProps={{ fontWeight: 700 }}
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Box>
      </Drawer>
      </ManagementPageShell>
    </Layout>
  );
}
